/** Planer i RCP — teksty obszaru RCP po angielsku; typowane względem polskich (brak klucza = błąd kompilacji). */
import { plnWorktimePl, type Shape } from './planner-worktime-pl';

export const plnWorktimeEn: Shape<typeof plnWorktimePl> = {
  title: 'Time & attendance',
  description: 'Labour Code timesheet: plan from the schedule, actuals from clock-ins, visits and logbooks, manager corrections',
  hero: {
    inWorkNow: 'At work now',
    worked: 'Worked this month',
    overtime: 'Overtime',
    alerts: 'Labour Code alerts',
    openOverLimit: 'Clock-ins over 16 h',
    openOverLimitHint: 'Open clock-ins longer than 16 h — need a manager correction',
  },
  tabs: {
    vacation: 'Leave',
    team: 'At work now',
    card: 'Employee timesheet',
    collective: 'Summary',
    periods: 'Settlement periods',
    profiles: 'Profiles',
  },
  month: { prev: 'Previous month', next: 'Next month', current: 'Current' },
  export: { pdf: 'Timesheet (PDF)', xlsx: 'Summary (XLSX)', done: 'File downloaded', failed: 'Could not download the file' },
  team: {
    title: 'Who is at work right now',
    description: 'Live clock-ins from the app and panel, with today’s planned shift',
    filterAll: 'Everyone',
    filterIn: 'At work',
    filterOut: 'Off work',
    search: 'Search person or department…',
    colPerson: 'Person',
    colStatus: 'Status',
    colPlan: 'Today’s plan',
    colSource: 'Today’s source',
    inWorkSince: 'At work since {time}',
    elapsed: '{duration} h',
    overLimit: 'over 16 h',
    outOfWork: 'Off work',
    noPlan: 'No shift',
    openCard: 'Timesheet',
    empty: 'Nobody matches the filter',
    emptyAll: 'No people in the team',
  },
  card: {
    people: 'Employees',
    searchPeople: 'Search employee…',
    noPeople: 'No employees in your scope',
    noMatch: 'Nobody matches the search',
    pick: 'Pick an employee from the list',
    balanceTitle: 'Month balance (h)',
    profileLine: '{system} · {etat} FTE · norm {daily} h/day',
    cols: {
      date: 'Date',
      plan: 'Plan',
      exec: 'Actual',
      source: 'Source',
      worked: 'Worked',
      ot50: 'OT 50%',
      ot100: 'OT 100%',
      night: 'Night',
      sundayHoliday: 'Sun/Hol',
      absence: 'Absent',
      notes: 'Notes',
    },
    colsHint: {
      ot50: 'Overtime with a 50% supplement',
      ot100: 'Overtime with a 100% supplement (night, Sunday, holiday, day off)',
      night: 'Hours in the night window',
      sundayHoliday: 'Hours on Sundays and holidays',
      absence: 'Absence hours (count towards the norm)',
    },
    correct: 'Correct day',
    adjustments: 'Corrections: {count}',
    today: 'Today',
    total: 'Total',
    legend: {
      title: 'Legend',
      sunday: 'Sunday',
      holiday: 'Holiday',
      absence: 'Absence',
      free: 'Day off',
      today: 'Today',
    },
  },
  summary: {
    nominal: 'Hours to work',
    nominalHint: 'Labour Code norm minus absences: {raw} h − {absence} h',
    worked: 'Worked',
    workedPct: '{pct}% of the norm',
    balance: 'Period balance',
    balanceHint: 'Worked minus the norm',
    balanceAsOf: 'As of the end of {date} — norm and work up to yesterday',
    overtimeLimit: 'Overtime limit',
    overtimeLimitHint: '{used} of {limit} h per year',
    ot50: 'Overtime 50%',
    ot100: 'Overtime 100%',
    night: 'Night time',
    sundayHoliday: 'Sundays & holidays',
    absence: 'Absences',
    onCall: 'On call',
  },
  alerts: {
    title: 'Labour Code compliance alerts',
    none: 'No breaches this month',
    period: 'Whole period',
    critical: 'Breach',
    warn: 'Warning',
  },
  correction: {
    title: 'Correct day',
    plan: 'Plan',
    planHint: 'Hours from the schedule — change only if the schedule was wrong',
    exec: 'Actual',
    execHint: 'End earlier than start = the shift ends on the next day',
    from: 'From',
    to: 'To',
    absence: 'Absence',
    absenceHint: 'An absence day has no working hours — the actual time will be cleared',
    noAbsence: 'None',
    onCall: 'On call (min)',
    notes: 'Notes',
    notesPlaceholder: 'e.g. remote work, business errand',
    status: 'Entry status',
    reason: 'Reason for correction',
    reasonRequired: 'Reason for correction — required, you are changing an existing entry',
    reasonPlaceholder: 'e.g. corrected after talking to the employee',
    preview: 'Calculation preview',
    previewHint: 'Calculated live with the same engine as the timesheet',
    previewBefore: 'Now',
    previewAfter: 'After correction',
    save: 'Save correction',
    saved: 'Correction saved',
    sourceNow: 'Current source:',
    priorityNote: 'A correction overrides app clock-ins, visits, logbooks and the schedule. Every change goes to the correction history.',
    errors: {
      planInvalid: 'Plan: enter both times as HH:MM or leave both fields empty',
      execInvalid: 'Actual: enter both times as HH:MM or leave both fields empty',
      execTooLong: 'Actual time cannot exceed 24 h',
      onCallInvalid: 'On call: whole minutes from 0 to 1440',
      reasonRequired: 'Give a reason for the correction',
      noChanges: 'Nothing changed',
    },
    historyTitle: 'Correction history',
    historyEmpty: 'No corrections for this day',
    historyLoading: 'Loading history…',
    historyBy: '{author} · {date}',
    fields: {
      planFrom: 'Plan from',
      planTo: 'Plan to',
      execFrom: 'Actual from',
      execTo: 'Actual to',
      absenceCode: 'Absence',
      onCallMinutes: 'On call (min)',
      status: 'Status',
      notes: 'Notes',
    },
    empty: 'empty',
  },
  collective: {
    search: 'Search employee or department…',
    department: 'Department',
    allDepartments: 'All departments',
    noDepartment: 'No department',
    sortLabel: 'Order',
    sort: {
      name: 'Surname A–Z',
      balanceAsc: 'Balance — largest shortfall',
      balanceDesc: 'Balance — largest surplus',
      worked: 'Worked — most',
      alerts: 'Alerts — most',
    },
    grouped: 'Group by department',
    colPerson: 'Employee',
    colWorked: 'Worked',
    colNominal: 'Norm',
    colBalance: 'Balance',
    colOvertime: 'OT',
    colAlerts: 'Alerts',
    total: 'Total',
    groupTotal: 'Total — {department}',
    empty: 'No data for this month',
    noMatch: 'Nobody matches the filters',
    cellHint: '{date}: {hours} h',
    shown: '{shown} of {total} people',
    openHint: 'Click a row to open the employee timesheet',
  },
  periods: {
    title: 'Settlement periods',
    description: 'A period (1, 3 or 4 months) sets the working-time norm, night window and annual overtime limit. Entries in a closed period can no longer be corrected.',
    add: 'New period',
    empty: 'No settlement periods',
    emptyHint: 'Add the first period so the timesheet calculates the norm and limits your way',
    cols: { name: 'Name', range: 'Range', length: 'Length', night: 'Night window', limit: 'Overtime limit', status: 'Status' },
    months: '{n} mo.',
    limit: '{h} h/year',
    close: 'Close period',
    reopen: 'Reopen',
    edit: 'Edit',
    closeConfirm: 'Close the period “{name}”? Once closed, entries in this period can no longer be corrected.',
    closed: 'Period closed',
    reopened: 'Period reopened',
    saved: 'Period saved',
    closedAt: 'closed {date}',
    dialog: {
      addTitle: 'New settlement period',
      editTitle: 'Edit settlement period',
      name: 'Name',
      namePlaceholder: 'e.g. Q4 2026',
      length: 'Period length',
      lengthOption: '{n} mo.',
      start: 'Start',
      end: 'End (calculated)',
      nightStart: 'Night from (hour)',
      nightEnd: 'Night to (hour)',
      nightHint: 'Labour Code art. 151⁷: 8 hours between 21:00 and 7:00 — set in the work rules',
      limit: 'Annual overtime limit (h)',
      limitHint: 'Labour Code art. 151 §3: 150 h a year unless the work rules say otherwise',
      rangeLocked: 'The date range cannot be changed once the period exists',
      invalid: 'Fill in all fields correctly',
    },
  },
  profiles: {
    title: 'Working-time profiles',
    description: 'The system, FTE and norms decide how the timesheet calculates the norm and overtime.',
    people: 'People',
    pick: 'Pick a person from the list',
    system: 'Working-time system',
    etat: 'FTE',
    etatHint: 'e.g. 1, 1/2, 3/4',
    dailyNorm: 'Daily norm (h)',
    weeklyNorm: 'Weekly norm (h)',
    active: 'Profile active',
    inactive: 'Profile inactive',
    regime: 'Regime: {regime}',
    saved: 'Profile saved',
    invalid: 'Check the FTE (e.g. 1 or 1/2) and norms (0–24 h, 0–168 h)',
    systemHint: {
      BASIC: '8 h a day and 40 h a week on average, five-day week',
      EQUIVALENT: 'Longer days (up to 12 h) balanced by shorter days or days off within the period',
      TASK_BASED: 'Time follows assigned tasks — the timesheet shows the norm, no overtime is calculated',
    },
  },
  my: {
    title: 'My working time',
    description: 'Your timesheet and clock-in / clock-out',
    clockIn: 'Clock in',
    clockOut: 'Clock out',
    inWorkSince: 'At work since {time}',
    notInWork: 'You are not at work right now',
    elapsed: '{duration} h since clock-in',
    planToday: 'Today’s plan',
    noPlanToday: 'No shift in the schedule',
    todayExec: 'Recorded today: {window}',
    clockedIn: 'Clocked in at {time}',
    clockedOut: 'Clocked out at {time}',
    clockHint: 'Clocking records the current time. Your manager fixes mistakes with a day correction.',
    overLimit: 'The clock-in has been open for over 16 h — ask your manager for a correction',
    cardTitle: 'My timesheet',
  },
  settings: {
    title: 'Working-time rules',
    description: 'Schedule checks while planning shifts — Labour Code thresholds the planner verifies',
    chipEnforce: 'Save blocking',
    chipOn: 'on',
    chipOff: 'off',
    chipRegime: 'Regime',
    enforce: {
      title: 'Block saving the schedule on a breach',
      on: 'A shift breaking a blocking threshold will not save — the planner can save it only with a justification. Warnings still only inform.',
      off: 'All breaches are warnings only — the planner shows them, but saving a shift is always possible.',
    },
    r2: {
      title: 'R2 — daily rest',
      law: 'Labour Code art. 132',
      explain: 'Between the end of a shift and the start of the next day’s shift the employee must have at least 11 hours of uninterrupted rest. Example: ending at 22:00 and starting at 6:00 gives only 8 h.',
      min: 'Minimum rest',
      minHint: 'Shorter rest ⇒ warning',
      reduced: 'Blocking threshold',
      reducedHint: 'Shorter rest ⇒ block (when enabled)',
      severityDefault: 'Default: below the blocking threshold — block, between thresholds — warning',
    },
    r4: {
      title: 'R4 — weekly working time',
      law: 'Labour Code art. 131',
      explain: 'Working time including overtime may not exceed 48 hours a week on average. The planner adds up shift hours Monday to Sunday.',
      warn: 'Warning threshold',
      warnHint: 'More hours in the week ⇒ warning',
      block: 'Blocking threshold',
      blockHint: 'More hours in the week ⇒ block (when enabled)',
      severityDefault: 'Default: above the blocking threshold — block, above the warning threshold — warning',
    },
    r5: {
      title: 'R5 — consecutive working days',
      law: 'Labour Code art. 133',
      explain: 'Every week the employee is entitled to at least 35 hours of uninterrupted rest. A long run of working days without a day off rules that out.',
      max: 'Max days in a row',
      maxHint: 'Longer run ⇒ warning',
      severityDefault: 'Default: warning. “Always block” stops runs that are too long.',
    },
    severity: {
      label: 'Severity',
      DEFAULT: 'Rule default',
      WARN: 'Always warning',
      BLOCK: 'Always block',
    },
    unitH: 'h',
    unitDays: 'days',
    errors: {
      invalid: 'Enter a value from {min} to {max}',
      reducedAboveMin: 'The blocking threshold cannot be higher than the minimum rest',
      blockBelowWarn: 'The blocking threshold cannot be lower than the warning threshold',
    },
    dirty: 'You have unsaved changes',
    clean: 'All saved',
    discard: 'Discard changes',
    save: 'Save rules',
    saved: 'Working-time rules saved',
    note: 'The rules evaluate the plan (schedule) when a shift is saved. Actual clock-ins are checked by the compliance alerts on the timesheet.',
  },
  errors: {
    generic: 'Something went wrong — please try again',
    network: 'No connection to the server',
    forbidden: 'You are not allowed to do this',
    loadFailed: 'Could not load the data',
    retry: 'Try again',
  },
};
